import { APITypes } from '../../../../primary-record-types';
import getName from '../../../../utils/getName';

/**
 * The key a note's origin health system groups under. Health systems group by name rather than by the portal
 * connection the note arrived through: a connection always reports one name, but a profile can hold several
 * connections to the SAME health system, and keying on the connection would split those into duplicate chips
 * that read identically. The connection id is the fallback for records that arrive without a name.
 */
const getOriginSourceKey = (originSource: APITypes.notesService.NoteOriginSource): string | null => {
    const key = originSource.label ?? originSource.id;
    return key ? `origin:${key}` : null;
};

/**
 * A note's "sources": where an imported note came from, or who wrote it, and — when it was created by an
 * organization member — the organization itself, so one chip can cover all of a pro's notes. Keys are namespaced
 * so connection ids, user ids, org ids, and source labels can't collide.
 */
export const getNoteSourceKeys = (note: APITypes.notesService.NoteListItem): string[] => {
    const keys: string[] = [];
    const originSourceKey = note.originSource ? getOriginSourceKey(note.originSource) : null;
    if (originSourceKey) {
        keys.push(originSourceKey);
    } else if (note.createdBySourceLabel) {
        keys.push(`label:${note.createdBySourceLabel}`);
    } else {
        keys.push(`user:${note.createUserId}`);
    }
    if (note.organizationId) keys.push(`org:${note.organizationId}`);
    return keys;
};

export type NoteSourceOption = {
    key: string;
    label: string;
    count: number;
};

/**
 * Distinct sources present in the loaded notes (people first, then the health systems and other external
 * sources notes were imported from, then organizations), each with its note count.
 */
export const getNoteSourceOptions = (
    notes: APITypes.notesService.NoteListItem[],
    getOrganizationName: (organizationId: string) => string | null,
): NoteSourceOption[] => {
    const options = new Map<string, NoteSourceOption>();
    for (const note of notes) {
        for (const key of getNoteSourceKeys(note)) {
            const existing = options.get(key);
            if (existing) {
                existing.count += 1;
                continue;
            }
            let label: string | null = null;
            if (key.startsWith('origin:')) label = note.originSource?.label || null;
            else if (key.startsWith('user:')) label = note.createdBy ? getName(note.createdBy) : null;
            else if (key.startsWith('label:')) label = note.createdBySourceLabel || null;
            else if (note.organizationId) label = getOrganizationName(note.organizationId);
            // An unresolvable source (e.g. a deleted user) gets no chip; its notes
            // still show whenever the filter allows them.
            if (!label) continue;
            options.set(key, { key, label, count: 1 });
        }
    }
    const rank = (key: string) =>
        key.startsWith('user:') ? 0 : key.startsWith('origin:') || key.startsWith('label:') ? 1 : 2;
    return Array.from(options.values()).sort((a, b) => rank(a.key) - rank(b.key) || a.label.localeCompare(b.label));
};

/** A note passes when no sources are selected, or any of its sources is selected. */
export const filterNotesBySource = (
    notes: APITypes.notesService.NoteListItem[],
    selectedKeys: string[] | null,
): APITypes.notesService.NoteListItem[] => {
    if (!selectedKeys?.length) return notes;
    const selected = new Set(selectedKeys);
    return notes.filter((note) => getNoteSourceKeys(note).some((key) => selected.has(key)));
};
